import { Button, Divider, Dropdown, InputNumber, Slider, Switch } from 'antd';
import DownloadStepperModal, { type DownloadStepperOption } from 'components/molecules/common/DownloadStepperModal';
import HeaderSortDescChevron from 'assets/svg/HeaderSortDescChevron';
import Loader from 'components/molecules/Loader';
import GenericWhitespaceGrid from 'components/organism/WhitespaceAnalysis/GenericWhitespaceGrid';
import StaticWhitespaceCard, { type StaticWhitespaceCardProps } from 'components/organism/WhitespaceAnalysis/StaticWhitespaceCard';
import { colorConstants } from 'config/colorConstants';
import { PPA_HOME_ROUTE } from 'config/urls';
import { FilterContextData } from 'contexts/FilterContext';
import { useWhitespaceAnalysisContext } from 'contexts/WhitespaceAnalysisContext';
import useWhitespaceCardPagination from 'hooks/useWhitespaceCardPagination';
import { debounce } from 'lodash';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { buildWhitespaceExportRows, WhitespaceExportFormat, WhitespaceExportScope } from './WhitespaceAnalysis/whitespaceExportConfig';
import {
    WhitespaceAnalysisBreadcrumb,
    WhitespaceAnalysisPageHeader,
    WhitespaceAxisRadioGroup,
} from './WhitespaceAnalysisHeader';
import { downloadExcel } from 'utils/ppaHelpers';
import { downloadCsv, formatWord, sendNotification } from 'utils/helperMethods';
const FILTER_BUTTON_CLASS =
    '!h-8 !rounded-md !border-black/15 !bg-white !px-3 !text-[14px] !font-normal !text-blackTitle !flex !justify-center !items-center';

type WhitespaceGridCell = Partial<StaticWhitespaceCardProps>;

const DOWNLOAD_STEP_ONE_OPTIONS: DownloadStepperOption[] = [
    { value: 'current_view', label: 'Current View' },
    // { value: 'opportunity_cells_only', label: 'Opportunity Cells Only' },
    // { value: 'all_filtered_cells', label: 'All Filtered Cells' },
];

const DOWNLOAD_STEP_TWO_OPTIONS: DownloadStepperOption[] = [
    { value: 'xlsx', label: 'download as .xlsx' },
    { value: 'csv', label: 'download as .csv' },
];

const WhitespaceAnalysis = () => {
    const navigate = useNavigate();
    const { allCheckedAttribute } = useContext(FilterContextData);
    const {
        title,
        tooltip,
        xAxis,
        setXAxis,
        yAxis,
        setYAxis,
        selectedYAxisKpiId,
        crossSection,
        setCrossSection,
        incrementValue,
        setIncrementValue,
        showWhitespaces,
        setShowWhitespaces,
        incrementConfig,
        xAxisOptions,
        yAxisOptions,
        crossSectionOptions,
        xAxisFilter,
        childLimit,
        gridRequestPayload,
        columnHeaders,
        gridRows,
        isGridFetched,
        loadMoreRows,
        loadMoreColumns,
        hasMoreRows,
        hasMoreColumns,
        isFetchingMoreRows,
        isFetchingMoreColumns,
        isMetaLoading,
        isGridFetching
    } = useWhitespaceAnalysisContext();

    const { getCellPaginationData, loadMoreForCell, collapseCellItems } = useWhitespaceCardPagination({
        gridRequestPayload,
        xAxisFilter,
        childLimit,
    });

    const [pendingXAxis, setPendingXAxis] = useState(xAxis);
    const [pendingYAxis, setPendingYAxis] = useState(yAxis);
    const [isAxesPopoverOpen, setIsAxesPopoverOpen] = useState(false);
    const [isCrossSectionOpen, setIsCrossSectionOpen] = useState(false);
    const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);

    const handleBack = useCallback(() => navigate(PPA_HOME_ROUTE), [navigate]);
    const handleOpenDownloadModal = useCallback(() => setIsDownloadModalOpen(true), []);
    const handleCloseDownloadModal = useCallback(() => setIsDownloadModalOpen(false), []);
    
    const handleDownloadExport = useCallback(async ({
        stepOneValue,
        stepTwoValue,
    }: {
        stepOneValue: string;
        stepTwoValue: string;
    }) => {
        const scope = stepOneValue as WhitespaceExportScope;
        const format = stepTwoValue as WhitespaceExportFormat;
        const exportGridRows = gridRows.map((row) => {
            const cells = columnHeaders.reduce<Record<string, WhitespaceGridCell | null>>((acc, columnHeader) => {
                const cell = row.cells[columnHeader] as WhitespaceGridCell | null | undefined;
                if (!cell) {
                    acc[columnHeader] = cell ?? null;
                    return acc;
                }
                const paginationData = getCellPaginationData({
                    bucketLabel: row.bucketLabel,
                    columnHeader,
                    defaultItems: cell.items ?? [],
                    defaultMoreCount: cell.moreCount ?? 0,
                });
                acc[columnHeader] = {
                    ...cell,
                    items: paginationData.items,
                    moreCount: paginationData.moreCount,
                };
                return acc;
            }, {});
            return {
                ...row,
                cells,
            };
        });
        const { headers, rows } = buildWhitespaceExportRows({
            gridRows: exportGridRows as any,
            columnHeaders,
            crossSection,
            scope,
            selectedYAxisKpiId,
        });

        if (rows.length === 0) {
            sendNotification('info', 'No data available to export for the selected option.');
            return;
        }

        const fileName = `whitespace_analysis_${new Date().toISOString().slice(0, 10)}`;
        const appliedFilterGroups = Object.entries(allCheckedAttribute || {})
            .map(([attributeType, selectedValues]) => ({
                label: formatWord(attributeType),
                items: Object.keys((selectedValues as Record<string, unknown>) || {}),
            }))
            .filter((group) => group.items.length > 0);
        const globalFiltersValue = appliedFilterGroups
            .map((group) => `${group.label}: ${group.items.join(', ')}`)
            .join(' | ');
        const exportMetadataRows: Array<Array<string | number>> = [
            ['Global filters', globalFiltersValue || 'No global filters selected'],
            ['X-Axis', xAxis || '-'],
            ['Y-Axis', yAxis || '-'],
            ['Cross Section', crossSection || '-'],
            ['Y-Axis increment', incrementValue],
            ['Show Whitespaces toggle', showWhitespaces ? 'ON' : 'OFF'],
            [],
        ];
        const exportRows = [...exportMetadataRows, headers, ...rows];

        try {
            if (format === 'xlsx') {
                await downloadExcel(fileName, exportRows, { moduleKey: 'whitespace_analysis' });
            } else {
                const csvEscape = (value: string | number) => {
                    const stringValue = String(value ?? '');
                    if (/[",\n]/.test(stringValue)) {
                        return `"${stringValue.replace(/"/g, '""')}"`;
                    }
                    return stringValue;
                };
                const csvContent = exportRows.map((row) => row.map(csvEscape).join(',')).join('\n');
                downloadCsv([csvContent], `${fileName}.csv`);
            }

            if (scope === 'all_filtered_cells') {
                sendNotification(
                    'info',
                    'Export includes currently loaded grid data. Scroll/load more rows and columns to include additional cells.',
                );
            } else {
                sendNotification('success', 'Export completed.');
            }
            setIsDownloadModalOpen(false);
        } catch (error) {
            sendNotification('error', 'Failed to export whitespace data. Please try again.');
        }
    }, [
        columnHeaders,
        crossSection,
        gridRows,
        allCheckedAttribute,
        getCellPaginationData,
        incrementValue,
        selectedYAxisKpiId,
        showWhitespaces,
        xAxis,
        yAxis,
    ]);

    const handleAxesPopoverOpenChange = useCallback((open: boolean) => {
        setIsAxesPopoverOpen(open);
        if (open) {
            setPendingXAxis(xAxis);
            setPendingYAxis(yAxis);
        }
    }, [xAxis, yAxis]);

    const handleApplyAxes = useCallback(() => {
        setXAxis(pendingXAxis);
        setYAxis(pendingYAxis);
        setIsAxesPopoverOpen(false);
    }, [pendingXAxis, pendingYAxis, setXAxis, setYAxis]);

    const handleCrossSectionSelect = useCallback((option: string) => {
        setCrossSection(option);
        setIsCrossSectionOpen(false);
    }, [setCrossSection]);
    const showDollarPrefix = useMemo(
        () => !['ounces', 'count'].includes(yAxis.trim().toLowerCase()),
        [yAxis],
    );

    const [localIncrementValue, setLocalIncrementValue] = useState(incrementValue);

    useEffect(() => {
        setLocalIncrementValue(incrementValue);
    }, [incrementValue]);

    const debouncedSetIncrementValue = useMemo(
        () => debounce((value: number) => setIncrementValue(value), 300),
        [setIncrementValue],
    );

    useEffect(() => () => debouncedSetIncrementValue.cancel(), [debouncedSetIncrementValue]);

    const handleIncrementChange = useCallback((value: number | [number, number]) => {
        const nextValue = typeof value === 'number' ? value : localIncrementValue;
        setLocalIncrementValue(nextValue);
        debouncedSetIncrementValue(nextValue);
    }, [debouncedSetIncrementValue, localIncrementValue]);

    const handleIncrementInputChange = useCallback((value: number | null) => {
        if (value == null) {
            return;
        }
        setLocalIncrementValue(value);
        debouncedSetIncrementValue(value);
    }, [debouncedSetIncrementValue]);

    const renderWhitespaceCell = useCallback(
        (cell: WhitespaceGridCell, rowLabel: string, columnHeader: string) => {
            const paginationData = getCellPaginationData({
                bucketLabel: rowLabel,
                columnHeader,
                defaultItems: cell.items ?? [],
                defaultMoreCount: cell.moreCount ?? 0,
            });

            return (
                <StaticWhitespaceCard
                    valueSales={cell.valueSales ?? ''}
                    valuePrefix={cell.valuePrefix ?? ''}
                    valueSuffix={cell.valueSuffix ?? ''}
                    trendValue={typeof cell.trendValue === 'number' ? cell.trendValue : null}
                    trendDirection={cell.trendDirection ?? 'neutral'}
                    avgValueSales={cell.avgValueSales ?? ''}
                    avgPrefix={cell.avgPrefix ?? ''}
                    shareLabel={cell.shareLabel ?? ''}
                    crossSectionShareLabel={cell.crossSectionShareLabel ?? ''}
                    crossSectionLabel={crossSection}
                    sharePercentage={cell.sharePercentage ?? 0}
                    items={paginationData.items}
                    moreCount={paginationData.moreCount}
                    heatmapColor={cell.heatmapColor ?? 'transparent'}
                    isWhitespace={showWhitespaces && Boolean(cell.isWhitespace)}
                    isMoreLoading={paginationData.isLoading}
                    showCollapseButton={paginationData.showCollapse}
                    selectedYAxisKpiId={selectedYAxisKpiId}
                    onCollapseClick={() =>
                        collapseCellItems({
                            bucketLabel: rowLabel,
                            columnHeader,
                        })
                    }
                    onMoreClick={() =>
                        loadMoreForCell({
                            bucketLabel: rowLabel,
                            columnHeader,
                            defaultItems: cell.items ?? [],
                            defaultMoreCount: cell.moreCount ?? 0,
                        })
                    }
                />
            );
        },
        [collapseCellItems, crossSection, getCellPaginationData, loadMoreForCell, selectedYAxisKpiId, showWhitespaces],
    );

    const axesPopoverContent = useMemo(() => (
        <div className="w-[360px] rounded-xl border border-solid border-[#e4e4e7] bg-white p-4 shadow-[0px_3px_6px_-4px_rgba(0,0,0,0.12),0px_9px_28px_8px_rgba(0,0,0,0.05)]">
            <div className="flex items-start gap-5">
                <WhitespaceAxisRadioGroup label="X-Axis" value={pendingXAxis} options={xAxisOptions} onChange={setPendingXAxis} />
                <Divider type="vertical" className="!h-[124px] !m-0" />
                <WhitespaceAxisRadioGroup label="Y-Axis" value={pendingYAxis} options={yAxisOptions} onChange={setPendingYAxis} />
            </div>
            <Divider className="!my-4" />
            <div className="flex justify-end">
                <Button
                    type="primary"
                    className="!h-8 !rounded-md !border-purpleFont !bg-purpleFont !px-4 !font-medium"
                    onClick={handleApplyAxes}
                >
                    Apply
                </Button>
            </div>
        </div>
    ), [handleApplyAxes, pendingXAxis, pendingYAxis, xAxisOptions, yAxisOptions]);

    const crossSectionDropdownContent = useMemo(() => (
        <div className="w-[160px] rounded-lg bg-white p-1 shadow-[0px_3px_6px_-4px_rgba(0,0,0,0.12),0px_9px_28px_8px_rgba(0,0,0,0.05)]">
            {crossSectionOptions.map((option) => {
                const isSelected = crossSection === option;
                return (
                    <button
                        key={option}
                        type="button"
                        className={`flex h-8 w-full items-center rounded px-3 text-left text-[14px] leading-[22px] ${isSelected ? 'bg-[#fbfaff] font-semibold text-purpleFont' : 'font-normal text-blackTitle'}`}
                        onClick={() => handleCrossSectionSelect(option)}
                    >
                        {option}
                    </button>
                );
            })}
        </div>
    ), [crossSection, crossSectionOptions, handleCrossSectionSelect]);


    if(isMetaLoading) {
        return (
            <div className="w-full h-[925px] bg-white rounded-lg">
                <Loader size="default" />
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-2 py-2">
            <WhitespaceAnalysisBreadcrumb title={title} />
            <div className="rounded-xl bg-white p-4">
                <WhitespaceAnalysisPageHeader
                    title={title}
                    onBack={handleBack}
                    tooltipContent={tooltip}
                    onDownloadClick={handleOpenDownloadModal}
                />
                <div className="mt-2 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <Dropdown
                            trigger={['click']}
                            placement="bottomLeft"
                            open={isAxesPopoverOpen}
                            onOpenChange={handleAxesPopoverOpenChange}
                            dropdownRender={() => axesPopoverContent}
                        >
                            <Button className={FILTER_BUTTON_CLASS}>
                                <span className="text-fontGray mr-2">Axes</span>
                                <HeaderSortDescChevron color={colorConstants.blackTitle} />
                            </Button>
                        </Dropdown>
                        <Dropdown
                            trigger={['click']}
                            placement="bottomLeft"
                            open={isCrossSectionOpen}
                            onOpenChange={setIsCrossSectionOpen}
                            dropdownRender={() => crossSectionDropdownContent}
                        >
                            <Button className={FILTER_BUTTON_CLASS}>
                                <span className="text-fontGray mr-1">Cross Section:</span>
                                <span className="text-fontGray mr-2">{crossSection}</span>
                                <HeaderSortDescChevron color={colorConstants.blackTitle} />
                            </Button>
                        </Dropdown>
                        <div className="flex items-center gap-2">
                            <span className="text-[14px] leading-[22px] text-blackTitle">{incrementConfig.label}</span>
                            <div className="w-[80px]">
                                <Slider
                                    min={incrementConfig.min}
                                    max={incrementConfig.max}
                                    step={incrementConfig.step}
                                    value={localIncrementValue}
                                    onChange={handleIncrementChange}
                                    railStyle={{ backgroundColor: '#BAB1E2' }}
                                    trackStyle={{ backgroundColor: '#BAB1E2' }}
                                    handleStyle={{ borderColor: '#BAB1E2' }}
                                />
                            </div>
                            <InputNumber
                                min={incrementConfig.min}
                                max={incrementConfig.max}
                                step={incrementConfig.step}
                                value={localIncrementValue}
                                prefix={showDollarPrefix ? '$' : undefined}
                                onChange={handleIncrementInputChange}
                                controls={false}
                                parser={(displayValue) => {
                                    const parsed = Number(displayValue?.replace(/[^\d.]/g, ''));
                                    return Number.isNaN(parsed) ? (incrementConfig.min as number) : parsed;
                                }}
                                className="!flex !h-8 !w-[80px] !items-center !overflow-hidden !rounded-md [&_.ant-input-number]:!h-full [&_.ant-input-number]:!border-none [&_.ant-input-number]:!bg-transparent [&_.ant-input-number]:!shadow-none [&_.ant-input-number-input]:!h-full [&_.ant-input-number-input]:!text-[14px] [&_.ant-input-number-input]:!leading-[22px] [&_.ant-input-number-input]:!text-blackTitle"
                            />
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Switch size="small" checked={showWhitespaces} onChange={setShowWhitespaces} />
                        <span className="text-[14px] leading-[22px] text-blackTitle">Show Whitespaces</span>
                    </div>
                </div>
                <div className="mt-4">
                    <GenericWhitespaceGrid
                        isFetched={isGridFetched}
                        columnHeaders={columnHeaders}
                        gridRows={gridRows}
                        renderCell={renderWhitespaceCell}
                        onLoadMoreRows={loadMoreRows}
                        onLoadMoreColumns={loadMoreColumns}
                        hasMoreRows={hasMoreRows}
                        hasMoreColumns={hasMoreColumns}
                        isLoadingMoreRows={isFetchingMoreRows}
                        isLoadingMoreColumns={isFetchingMoreColumns}
                        isGridFetching={isGridFetching}
                    />
                </div>
            </div>
            <DownloadStepperModal
                open={isDownloadModalOpen}
                onClose={handleCloseDownloadModal}
                onExport={handleDownloadExport}
                stepOneOptions={DOWNLOAD_STEP_ONE_OPTIONS}
                stepTwoOptions={DOWNLOAD_STEP_TWO_OPTIONS}
            />
        </div>
    );
};

export default WhitespaceAnalysis;
